import { Link } from "react-router-dom";
import ScrollToTop from "@/components/feature/ScrollToTop";
import BackToTop from "@/components/feature/BackToTop";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";
import { brand, credibilityRules, stats, testimonials } from "@/mocks/site";

const principles = [
  {
    icon: "ri-focus-3-line",
    title: "只评证据，不评人",
    desc: "可信度分数衡量的是材料本身能否支撑结论，不用于评判任何人的品行，也不做道德裁判。",
  },
  {
    icon: "ri-link-unlink-m",
    title: "拒绝相关即因果",
    desc: "同款、同城、互关这类弱证据，必须先排除更合理的解释，才会进入结论环节。",
  },
  {
    icon: "ri-shield-keyhole-line",
    title: "守住隐私边界",
    desc: "婚姻登记、医疗信息、家庭住址与未成年人相关信息一律不进入鉴定范围。",
  },
  {
    icon: "ri-refresh-line",
    title: "结论可更新",
    desc: "出现新材料时公开修订，并保留历史结论与修改时间，不做静默修改。",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      name: "关于我们 · 瓜田爆料",
      url: `${SITE_ORIGIN}/about`,
      description:
        "瓜田爆料是专注爆料可信度鉴定的内容门户，公开评估维度、鉴定方法与内容原则，帮助读者分辨传闻真伪。",
      inLanguage: "zh-CN",
    },
    {
      "@type": "Organization",
      name: brand.name,
      url: SITE_ORIGIN,
      email: brand.email,
      description: brand.description,
      knowsAbout: brand.keywords,
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
        { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_ORIGIN}/about` },
      ],
    },
  ],
};

export default function About() {
  useSeo({
    title: "关于我们 | 鉴定方法与内容原则 · 瓜田爆料",
    description:
      "了解瓜田爆料：我们如何做真假鉴定与可信度评估，评估维度、鉴定流程、内容原则与隐私边界是什么，以及读者给出的反馈。",
    keywords: ["关于瓜田爆料", "鉴定方法", "可信度评估", "证据链整理", "内容原则"],
    path: "/about",
    ogType: "website",
    jsonLd,
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <ScrollToTop />
      <SiteHeader />
      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100 paper-texture">
          <div className="max-w-shell mx-auto px-4 md:px-6 py-8 md:py-14">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <h1 className="mt-5 font-heading text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight max-w-3xl">
              关于 {brand.name}
            </h1>
            <p className="mt-4 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">
              {brand.description}
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-background-300 pt-6">
              {stats.map((s) => (
                <div key={s.label}>
                  <p className="font-mono-num text-xl md:text-2xl font-bold text-foreground-950">
                    {s.value}
                  </p>
                  <p className="mt-1 text-xs text-foreground-500 leading-relaxed">{s.note}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-shell mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-5">
              <p className="text-xs font-medium tracking-[0.2em] text-primary-600 uppercase">
                网站定位
              </p>
              <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                我们只做一件事：把传闻拆成可以核对的证据
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              <p>
                互联网上的爆料往往是一张截图、一句转述，配上情绪浓烈的标题。读者真正缺的不是更多传闻，
                而是一个能快速判断「这条能不能信」的地方。
              </p>
              <p>
                所以 {brand.name} 把每条爆料拆成结构化的档案：线索来自哪里、属于什么等级、有没有彼此独立的来源相互印证、
                当事方有没有回应。把这些写清楚，可信度就不再是一个凭感觉的词。
              </p>
              <p>
                我们不生产爆料，也不替任何一方站台。当证据不足时，我们会明说「还缺什么」，
                而不是用模糊的措辞制造确定感。这是我们对读者的基本承诺。
              </p>
            </div>
          </div>
        </section>

        <section id="method" className="border-y border-background-200 bg-background-100 scroll-mt-28">
          <div className="max-w-shell mx-auto px-4 md:px-6 py-12 md:py-16">
            <p className="text-xs font-medium tracking-[0.2em] text-primary-600 uppercase">
              鉴定方法
            </p>
            <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
              可信度是怎么算出来的
            </h2>
            <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-600 leading-relaxed">
              四个维度分别打分，再按权重合成 0-100 的分数，最后对应一个结论标签。
              任何人都可以按同样的路径复核我们的判断。
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {[
                {
                  title: "源头可查性",
                  desc: "线索能否追溯到可回看、可核实的原始出处，而不是只有转述。",
                },
                {
                  title: "证据链完整度",
                  desc: "从线索到结论之间的每一环是否有材料支撑，是否存在关键断点。",
                },
                {
                  title: "多方交叉印证",
                  desc: "是否有彼此独立的来源指向同一结论，并排查同源转发造成的假印证。",
                },
              ].map((d) => (
                <div key={d.title} className="border border-background-200 rounded-card bg-background-50 p-5">
                  <h3 className="font-heading text-base font-bold text-foreground-950">{d.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{d.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 border border-background-200 rounded-card overflow-hidden bg-background-50">
              <table className="w-full text-sm">
                <thead className="bg-background-100 text-foreground-600">
                  <tr>
                    <th className="text-left font-medium px-4 md:px-6 py-3">分数区间</th>
                    <th className="text-left font-medium px-4 md:px-6 py-3">结论标签</th>
                    <th className="text-left font-medium px-4 md:px-6 py-3 hidden sm:table-cell">判定说明</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200">
                  {credibilityRules.map((rule) => (
                    <tr key={rule.verdict}>
                      <td className="px-4 md:px-6 py-4 font-mono-num text-foreground-900">{rule.level}</td>
                      <td className="px-4 md:px-6 py-4">
                        <span className="px-2.5 py-1 rounded-control bg-secondary-100 text-secondary-800 text-xs">
                          {rule.verdict}
                        </span>
                      </td>
                      <td className="px-4 md:px-6 py-4 text-foreground-600 hidden sm:table-cell">
                        {rule.desc}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section id="principle" className="max-w-shell mx-auto px-4 md:px-6 py-12 md:py-16 scroll-mt-28">
          <p className="text-xs font-medium tracking-[0.2em] text-primary-600 uppercase">
            内容原则
          </p>
          <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
            内容原则：哪些事我们一定不做
          </h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {principles.map((p) => (
              <div key={p.title} className="flex gap-4 border border-background-200 rounded-card bg-background-50 p-5">
                <span className="shrink-0 w-11 h-11 flex items-center justify-center rounded-control bg-accent-100 text-accent-800">
                  <i className={`${p.icon} text-xl`}></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{p.title}</h3>
                  <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="max-w-shell mx-auto px-4 md:px-6 py-12 md:py-16">
            <p className="text-xs font-medium tracking-[0.2em] text-primary-600 uppercase">
              读者口碑
            </p>
            <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
              他们这样用瓜田爆料
            </h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {testimonials.map((t) => (
                <figure key={t.name} className="border border-background-200 rounded-card bg-background-50 p-5 md:p-6">
                  <blockquote className="text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                    {t.text}
                  </blockquote>
                  <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 font-heading text-sm font-bold">
                      {t.name.slice(0, 1)}
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">{t.name}</span>
                      <span className="block text-xs text-foreground-500">{t.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-shell mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="rounded-card border border-background-200 bg-background-50 p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                想投稿或提出问题？
              </h2>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                线索投稿：{brand.tipEmail} · 内容合作：{brand.cooperateEmail}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <Link
                to="/contact"
                className="px-6 py-3 rounded-control bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors text-center whitespace-nowrap"
              >
                联系我们
              </Link>
              <Link
                to="/faq"
                className="px-6 py-3 rounded-control border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors text-center whitespace-nowrap"
              >
                常见问题
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}